import { Box, Divider, Grid, Paper, Skeleton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { NumericFormat } from "react-number-format";
import { CostsCalculations } from "../../../../types/costsCalculations";
import { CostsConfig } from "../../../../types/costsConfig";

export default function OverallCosts({
  costsConfig,
  costsCalculations,
  height,
}: {
  costsConfig: CostsConfig | undefined;
  costsCalculations: CostsCalculations | undefined;
  height: number;
}) {
  const theme = useTheme();

  // Fallback to Skeleton if data is not ready
  if (!costsConfig || !costsCalculations) {
    return (
      <Skeleton
        sx={{
          borderRadius: "5px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
        }}
        animation="pulse"
        variant="rectangular"
        width={"100%"}
        height={height}
      />
    );
  }

  return (
    <Paper sx={{ borderRadius: "5px", height: height }}>
      <Grid
        container
        spacing={1}
        flexDirection={"column"}
        sx={{ height: "100%", padding: 2 }}
      >
        <Grid
          item
          flexDirection={"row"}
          spacing={2}
          container
          alignItems={"center"}
        >
          <Grid item>
            <Typography variant="h5">ROI & Savings</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1" color={"gray"}>
              ({costsConfig.organizationName})
            </Typography>
          </Grid>
        </Grid>

        <Grid item xs>
          <Grid
            container
            alignContent={"space-around"}
            alignItems={"center"}
            sx={{ height: "100%", padding: "5px 0px 0px 30px" }}
            columnSpacing={1}
          >
            <Grid xs={4}>
              <Grid
                container
                flexDirection={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Grid item>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.secondaryChart,
                      width: 40,
                      height: 20,
                      borderRadius: 1,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    D
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography>
                    Estimated Direct Costs of Sick Days per Year
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={2}></Grid>
            <Grid xs={3}>
              <Typography variant="h6" component="span">
                <NumericFormat
                  value={costsCalculations.directCostsPerYear}
                  suffix=" USD"
                  allowLeadingZeros
                  thousandSeparator=","
                  displayType="text" // Ensures it renders as plain text
                />
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography></Typography>
            </Grid>

            <Grid xs={4}>
              <Grid
                container
                flexDirection={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Grid item>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.secondaryChart,
                      width: 40,
                      height: 20,
                      borderRadius: 1,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    I
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography>
                    Estimated Indirect Costs of Sick Days per Year
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={2} sx={{ textAlign: "center" }}>
              <Typography sx={{ color: "gray" }}>x{costsConfig.indirectCostsMultiplier}</Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant="h6" component="span">
                <NumericFormat
                  value={costsCalculations.indirectCostsPerYear}
                  suffix=" USD"
                  allowLeadingZeros
                  thousandSeparator=","
                  displayType="text" // Ensures it renders as plain text
                />
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography></Typography>
            </Grid>

            <Grid xs={4}>
              <Grid
                container
                flexDirection={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Grid item>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.secondaryChart,
                      width: 40,
                      height: 20,
                      borderRadius: 1,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    MSD
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography> Costs Related to MSDs </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={2} sx={{ textAlign: "center" }}>
              <Typography sx={{ color: "gray" }}>{costsConfig.msdPercentage}%</Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant="h6" component="span">
                <NumericFormat
                  value={costsCalculations.costsMSD}
                  suffix=" USD"
                  allowLeadingZeros
                  thousandSeparator=","
                  displayType="text" // Ensures it renders as plain text
                />
              </Typography>
            </Grid>
            <Grid xs={1}>
              <Typography>/</Typography>
            </Grid>
            <Grid xs={2}>
              <Typography>{costsCalculations.daysMSD} days</Typography>
            </Grid>
            <Grid xs={12}>
              <Divider sx={{ borderBottomWidth: 1 }} />
            </Grid>
            <Grid xs={4}>
              <Grid
                container
                flexDirection={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Grid item>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primaryChart,
                      width: 40,
                      height: 20,
                      borderRadius: 1,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    MLI
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography>
                    Potential MSD Costs Covered by the MLI Solution
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={2} sx={{ textAlign: "center" }}>
              <Typography sx={{ color: "gray" }}>60%</Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant="h6" component="span">
                <NumericFormat
                  value={costsCalculations.costsMLIRelated}
                  suffix=" USD"
                  allowLeadingZeros
                  thousandSeparator=","
                  displayType="text" // Ensures it renders as plain text
                />
              </Typography>
            </Grid>
            <Grid xs={1}>
              <Typography>/</Typography>
            </Grid>
            <Grid xs={2}>
              <Typography>{costsCalculations.daysMLIRelated} days</Typography>
            </Grid>
            <Grid xs={4}>
              <Grid
                container
                flexDirection={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Grid item>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primaryChart,
                      width: 40,
                      height: 20,
                      borderRadius: 1,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    EFF
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography>Effect of the MLI® Solution</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={2} sx={{ textAlign: "center" }}>
              <Typography sx={{ color: "gray" }}>15%</Typography>
            </Grid>

            <Grid xs={3}>
              <Typography variant="h6" component="span">
                <NumericFormat
                  value={costsCalculations.costsEffect}
                  suffix=" USD"
                  allowLeadingZeros
                  thousandSeparator=","
                  displayType="text" // Ensures it renders as plain text
                />
              </Typography>
            </Grid>
            <Grid xs={1}>
              <Typography>/</Typography>
            </Grid>
            <Grid xs={2}>
              <Typography>{costsCalculations.daysEffect} days</Typography>
            </Grid>
            <Grid xs={12}>
              <Divider sx={{ borderBottomWidth: 1 }} />
            </Grid>

            <Grid item xs={4}>
              <Typography>PRECURE Subscription</Typography>
            </Grid>
            <Grid xs={2}></Grid>
            <Grid xs={4}>
              {costsConfig.nBacks} MLI® Back + {costsConfig.nElbows} MLI® Elbow
              for ~{costsCalculations.numEmployees} employees collecting data
            </Grid>
            <Grid xs={2}></Grid>
            <Grid xs={4}>
              <Grid
                container
                flexDirection={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Grid item>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.terciaryChart,
                      width: 40,
                      height: 20,
                      borderRadius: 1,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    SUB
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography>Estimated Yearly Subscription Cost</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={2}></Grid>
            <Grid xs={6}>
              <Typography variant="h6" component="span">
                <NumericFormat
                  value={costsCalculations.subsCost}
                  suffix=" USD"
                  allowLeadingZeros
                  thousandSeparator=","
                  displayType="text" // Ensures it renders as plain text
                />
              </Typography>
            </Grid>
            <Grid xs={12}>
              <Divider sx={{ borderBottomWidth: 1 }} />
            </Grid>

            <Grid xs={4}>
              <Grid
                container
                flexDirection={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Grid item>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primaryChart,
                      width: 40,
                      height: 20,
                      borderRadius: 1,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    SAV
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography>Savings</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={2} sx={{ textAlign: "center" }}>
              <Typography sx={{ color: "gray" }}></Typography>
            </Grid>

            <Grid xs={3}>
              <Typography variant="h6" component="span">
                <NumericFormat
                  value={costsCalculations.savings}
                  suffix=" USD"
                  allowLeadingZeros
                  thousandSeparator=","
                  displayType="text" // Ensures it renders as plain text
                />
              </Typography>
            </Grid>
            <Grid xs={1}></Grid>
            <Grid xs={2}></Grid>

            <Grid xs={4}>
              <Grid
                container
                flexDirection={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Grid item>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primaryChart,
                      width: 40,
                      height: 20,
                      borderRadius: 1,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    ROI
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography>Return on Investment</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={2} sx={{ textAlign: "center" }}>
              <Typography sx={{ color: "gray" }}></Typography>
            </Grid>

            <Grid xs={3}>
              <Typography variant="h6" component="span">
                <NumericFormat
                  value={(costsCalculations.valueROI * 100).toFixed(2)}
                  suffix=" %"
                  allowLeadingZeros
                  thousandSeparator=","
                  displayType="text" // Ensures it renders as plain text
                />
              </Typography>
            </Grid>
            <Grid xs={1}></Grid>
            <Grid xs={2}></Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}
